// `.epd` (EAT Photo Design) files (DRAW_PLAN §1, §4): a zip with document.json, assets/<hash>.<ext> (images
// and fonts, by content hash) and thumb.png. fflate (MIT) is already a dependency.
import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import { parseDocFile, serializeDoc } from './document';
import type { Doc } from './types';

export type EpdContents = { doc: Doc; assets: Map<string, Uint8Array>; thumb: Uint8Array | null };

export function writeEpd(
  doc: Doc,
  assets: Map<string, Uint8Array> = new Map(),
  thumb: Uint8Array | null = null,
): Uint8Array {
  const files: Zippable = { 'document.json': strToU8(serializeDoc(doc)) };
  // Assets are already compressed (PNG/JPEG/WOFF2) or small: store them; deflate only the JSON.
  for (const [name, bytes] of assets) files[`assets/${name}`] = [bytes, { level: 0 }];
  if (thumb) files['thumb.png'] = [thumb, { level: 0 }];
  return zipSync(files, { level: 6 });
}

export function readEpd(bytes: Uint8Array): EpdContents {
  const files = unzipSync(bytes);
  const json = files['document.json'];
  if (!json) throw new Error('not an .epd file: no document.json');
  const { doc } = parseDocFile(strFromU8(json));
  const assets = new Map<string, Uint8Array>();
  for (const [name, data] of Object.entries(files))
    if (name.startsWith('assets/') && name.length > 7) assets.set(name.slice(7), data);
  return { doc, assets, thumb: files['thumb.png'] ?? null };
}
